import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, of } from 'rxjs';
import { catchError, finalize, mergeMap } from 'rxjs/operators';
import { ToastService } from '../../../../services/toast-service';
import { Document } from '../../../../shared/document-directive';
import { DocumentTableComponent } from '../../../../shared/document-table-component/document-table-component';
import { IngestionDto, UploadItem } from '../../../../shared/interfaces';
import { DuplicatesActionModalComponent } from './duplicates-action-modal/duplicates-action-modal.component';
import { IngestionModalComponent } from './ingestion-modal/ingestion-modal';

@Component({
  selector: 'app-to-process',
  standalone: true,
  imports: [CommonModule, DocumentTableComponent, NgbDropdownModule],
  templateUrl: './to-process.html',
  styleUrl: './to-process.scss',
})
export class ToProcess extends Document {
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);

  status: string = 'input';
  task: string = 'trigger-scan';
  sourceType: string = 'input';

  // Limiti per l'upload
  readonly MAX_FILES = 1500;
  readonly MAX_CONCURRENT_UPLOADS = 5;

  isUploadMenuOpen = signal(false);
  isUploading = signal(false);
  isDragging = signal(false);
  uploadProgress = signal({ completed: 0, total: 0 });
  private dragCounter = 0;

  openIngestionModal() {
    const index = this.rootFolderName();
    if (!index) return;
    const modalRef = this.modalService.open(IngestionModalComponent, { centered: true, windowClass: 'agentic-custom-modal' });
    modalRef.componentInstance.indexName = index;
    modalRef.result.then(res => res && this.confirmIngestion(res)).catch(() => {});
  }

  confirmIngestion(indexName: string) {
    const selectedDocs = this.selectedItems().map(doc => doc.percorso);

    const payload: IngestionDto = { 
      indexName: indexName,
      docs: selectedDocs,
      action: 'replace'
    };

    this.checkDuplicatesOrExecute(payload);
  }

  private checkDuplicatesOrExecute(payload: IngestionDto){
    this.isLoading.set(true);

    this.documentService.checkDuplicates(payload).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (res.hashConflicts.length > 0 || res.pathConflicts.length > 0) {
          this.handleDuplicatesModal(payload, res);
        } else {
          this.executeIngestion(payload);
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  private executeIngestion(payload: IngestionDto) {
    this.isLoading.set(true);

    this.documentService.ingestDocuments(this.task, payload).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.selectedItems.set([]); 
      },
      error: () => this.isLoading.set(false)
    });
  }

  private handleDuplicatesModal(originalPayload: IngestionDto, res: any) {
    const modalRef = this.modalService.open(DuplicatesActionModalComponent, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    modalRef.componentInstance.hashDuplicatesList = res.hashConflicts;
    modalRef.componentInstance.pathDuplicatesList = res.pathConflicts;
    modalRef.componentInstance.hashConflict = res.hashConflicts.length > 0;
    modalRef.componentInstance.pathConflict = res.pathConflicts.length > 0;
    
    const totalDocsSent = originalPayload.docs?.length || 0;
    modalRef.componentInstance.allDuplicates = (totalDocsSent === (res.pathConflicts.length + res.hashConflicts.length));

    modalRef.result.then((userChoice: 'replace' | 'keep' | 'ignore' | 'cancel') => {
      if (!userChoice || userChoice === 'cancel') return; 

      if (userChoice === 'replace') {
        this.executeIngestion({ ...originalPayload, action: 'replace' });
      } else if (userChoice === 'keep') {
        this.executeIngestion({ ...originalPayload, action: 'keep' });
      } else if (userChoice === 'ignore') {
        
        const conflicts = [...res.hashConflicts, ...res.pathConflicts];
        const duplicateIncomingPaths = new Set(conflicts.map((c: any) => c.incomingFile));
        const filteredDocs = originalPayload.docs?.filter(doc => !duplicateIncomingPaths.has(doc)) || [];
        
        if (filteredDocs.length === 0) {
          this.toastService.show('Tutti i file selezionati erano duplicati. Operazione ignorata.', 'info');
          this.selectedItems.set([]); 
          return;
        }
        
        this.executeIngestion({ indexName: originalPayload.indexName, docs: filteredDocs, action: 'replace' });
      }
    }).catch(() => {});
  }

  // --- Funzioni Upload UI ---
  toggleUploadMenu() {
    if (!this.isInFolder()) return;
    this.isUploadMenuOpen.update(v => !v);
  }

  triggerFileInput(inputElement: HTMLInputElement) {
    this.isUploadMenuOpen.set(false);
    inputElement.click();
  }

  // --- Drag & Drop ---
  onDragEnter(e: DragEvent) { 
    if (!this.isInFolder()) {
      return; 
    }
    e.preventDefault(); 
    this.dragCounter++; 
    this.isDragging.set(true); 
  }

  onDragOver(e: DragEvent) { 
    if (this.isInFolder()) {
      e.preventDefault(); 
    }
  }

  onDragLeave(e: DragEvent) { 
    if (!this.isInFolder()) {
      return; 
    }
    this.dragCounter--; 
    if (this.dragCounter === 0) {
      this.isDragging.set(false); 
    }
  }

  async onDrop(e: DragEvent) {
    if (!this.isInFolder()) return;
    e.preventDefault(); this.dragCounter = 0; this.isDragging.set(false);
    if (!e.dataTransfer?.items) return;
    this.isUploading.set(true);
    try {
      const uploadItems: UploadItem[] = [];
      const base = this.currentPath().replace(/^input\//, '');
      const promises = Array.from(e.dataTransfer.items)
        .filter((i: any) => i.kind === 'file')
        .map((i: any) => this.traverseFileTree(i.webkitGetAsEntry(), base, uploadItems));
      await Promise.all(promises);
      this.startUploadProcess(uploadItems);
    } catch { this.isUploading.set(false); }
  }

  private traverseFileTree(item: any, path: string, result: UploadItem[]): Promise<void> {
    return new Promise((resolve) => {
      if (item.isFile) {
        item.file((f: File) => {
          if (!f.name.startsWith('.')) result.push({ file: f, path: path + f.name });
          resolve();
        });
      } else if (item.isDirectory) {
        const reader = item.createReader();
        reader.readEntries(async (entries: any[]) => {
          await Promise.all(entries.map((e: any) => this.traverseFileTree(e, path + item.name + '/', result)));
          resolve();
        });
      }
    });
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const base = this.currentPath().replace(/^input\//, '');
    const items = Array.from(input.files).filter((f: File) => !f.name.startsWith('.')).map((f: File) => ({
      file: f, path: base + (f.webkitRelativePath || f.name)
    }));
    this.startUploadProcess(items);
  }

  private startUploadProcess(items: UploadItem[]) {
    if (!items.length) { this.isUploading.set(false); return; }
    
    // Controllo limite file
    if (items.length > this.MAX_FILES) {
      this.toastService.show(`Puoi caricare massimo ${this.MAX_FILES} file alla volta.`, 'error');
      this.isUploading.set(false);
      return;
    }

    this.isUploading.set(true);
    this.uploadProgress.set({ completed: 0, total: items.length });
    
    from(items).pipe(
      mergeMap((i: UploadItem) => this.documentService.uploadSingleFile(i.file, i.path).pipe(catchError(() => of(null))), this.MAX_CONCURRENT_UPLOADS),
      finalize(() => { this.isUploading.set(false); this.loadData(); })
    ).subscribe(() => this.uploadProgress.update(p => ({ ...p, completed: p.completed + 1 })));
  }
}